import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Layers,
  Lock,
  Send,
  Sparkles,
  Ticket,
  RefreshCw,
} from "lucide-react";
import {
  Affix,
  Btn,
  Card,
  CardBody,
  CardHeader,
  Field,
  FormBar,
  Input,
  Seg,
  Select,
  ToggleRow,
} from "../../loyalty/components/primitives";
import successToast from "../../../../components/helper-components/success-toast";
import errorToast from "../../../../components/helper-components/error-toast";
import {
  couponCreateAction,
  couponUpdateAction,
} from "../../../../redux/actions/coupon-action";

const DISCOUNT_TYPES = [
  { value: "percent", label: "Percent" },
  { value: "flat", label: "Flat" },
  { value: "bogo", label: "BOGO" },
  { value: "tiered", label: "Tiered" },
];

const STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "scheduled", label: "Scheduled" },
  { value: "active", label: "Active" },
];

const SCOPE_OPTIONS = [
  { value: "all", label: "All products" },
  { value: "category", label: "Category" },
  { value: "brand", label: "Brand" },
  { value: "product", label: "Specific products" },
];

const CHANNELS = [
  { value: "sms", label: "SMS" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "email", label: "Email" },
  { value: "printed", label: "Printed" },
  { value: "manual", label: "Manual" },
];

const CHARSETS = [
  { value: "alphanum", label: "Alphanumeric (no 0,O,1,I)" },
  { value: "alpha", label: "Alphabetic only" },
  { value: "numeric", label: "Numeric only" },
];

// ISO yyyy-mm-dd <-> epoch ms helpers.
const msToInputDate = (ms) => {
  if (!ms) return "";
  const d = new Date(Number(ms));
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const inputDateToMs = (s, endOfDay = false) => {
  if (!s) return null;
  const [yyyy, mm, dd] = s.split("-").map((v) => parseInt(v, 10));
  if (!yyyy || !mm || !dd) return null;
  const d = endOfDay
    ? new Date(yyyy, mm - 1, dd, 23, 59, 59, 999)
    : new Date(yyyy, mm - 1, dd, 0, 0, 0, 0);
  return d.getTime();
};

const upperSlug = (s) =>
  (s || "").toString().toUpperCase().replace(/[^A-Z0-9_-]/g, "").slice(0, 32);

const blankCoupon = () => {
  const now = Date.now();
  const inThirtyDays = now + 30 * 24 * 60 * 60 * 1000;
  return {
    mode: "single",
    coupon: {
      name: "",
      description: "",
      status: "draft",
      discount_type: "percent",
      valid_from: now,
      valid_to: inThirtyDays,
      config: {
        limits: {
          max_uses: null,
          per_user: true,
          per_user_limit: 1,
          uses_per_code: 1,
          min_purchase: 0,
          min_qty: 0,
        },
        scope: {
          type: "all",
          ref_ids: [],
          eligible_tiers: ["ALL"],
          first_purchase_only: false,
        },
        stacking: {
          with_other_coupons: false,
          with_points: true,
          earn_on_this: false,
        },
        return_policy: {
          non_returnable: false,
          no_exchange: false,
          show_warning_at_billing: true,
        },
        discount: {
          kind: "percent",
          value: 20,
          max_cap: null,
        },
      },
    },
    single: {
      code: "",
      case_insensitive: true,
    },
    series: {
      generation_config: {
        series_name: "",
        distribution_channel: "sms",
        prefix: "",
        code_length: 8,
        code_charset: "alphanum",
        generation_quantity: 1000,
        uses_per_code: 1,
        valid_from_override: null,
        valid_to_override: null,
      },
    },
  };
};

// Build the payload envelope per docs §16.
const buildEnvelope = (state) => {
  const base = {
    mode: state.mode,
    coupon: {
      name: state.coupon.name.trim(),
      description: state.coupon.description?.trim() || undefined,
      status: state.coupon.status,
      discount_type: state.coupon.discount_type,
      valid_from: Number(state.coupon.valid_from),
      valid_to: Number(state.coupon.valid_to),
      config: state.coupon.config,
    },
  };
  if (state.mode === "single") {
    return {
      ...base,
      single: {
        code: upperSlug(state.single.code),
        case_insensitive: !!state.single.case_insensitive,
      },
    };
  }
  return {
    ...base,
    series: {
      generation_config: {
        ...state.series.generation_config,
        prefix: state.series.generation_config.prefix || "",
      },
    },
  };
};

// Reverse-engineer the envelope into form state when editing.
// Series detection is keyed on the backend's series_id column (returned by
// listCoupons via a LEFT JOIN on coupon_series). Single-mode coupons get a
// single_code subselect from coupon_code in the same query — we read both
// without a second fetch.
const hydrateFromCoupon = (row) => {
  const base = blankCoupon();
  if (!row) return base;
  const isSeries = !!(row.series_id || row.series);
  const mode = isSeries ? "series" : "single";
  const hydrated = {
    ...base,
    mode,
    coupon: {
      name: row.name || "",
      description: row.description || "",
      status: row.status === "expired" || row.status === "archived" ? "draft" : row.status || "draft",
      discount_type: row.discount_type || "percent",
      valid_from: row.valid_from || base.coupon.valid_from,
      valid_to: row.valid_to || base.coupon.valid_to,
      config: { ...base.coupon.config, ...(row.config || {}) },
    },
  };
  if (mode === "single") {
    // Backend returns the literal code as `single_code` on the list row.
    // `row.code` is a fallback for older shapes; `row.config.single_code`
    // is the seeder's convenience field.
    hydrated.single = {
      code:
        (typeof row.single_code === "string" && row.single_code) ||
        row.code ||
        row.config?.single_code ||
        "",
      case_insensitive: row.case_insensitive ?? true,
    };
  } else {
    // Series generation_config can come from a nested series row OR be
    // synthesized from the list's series_target / series_generated.
    const seriesCfg =
      (row.series && row.series.generation_config) ||
      row.config?.series ||
      {};
    hydrated.series = {
      generation_config: {
        ...base.series.generation_config,
        ...seriesCfg,
        // Surface live counters too so the form's preview reads correctly.
        generation_quantity:
          Number(
            seriesCfg.generation_quantity ??
              seriesCfg.count ??
              row.series_target ??
              base.series.generation_config.generation_quantity ??
              0
          ) || 0,
        prefix: seriesCfg.prefix || base.series.generation_config.prefix || "",
      },
    };
  }
  return hydrated;
};

const CreateTab = ({ editing, onCancel, onSaved }) => {
  const dispatch = useDispatch();
  const [state, setState] = useState(() => hydrateFromCoupon(editing));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setState(hydrateFromCoupon(editing));
  }, [editing]);

  const isEdit = !!editing?.id;

  const setMode = (mode) => setState((s) => ({ ...s, mode }));

  const setCoupon = (patch) =>
    setState((s) => ({ ...s, coupon: { ...s.coupon, ...patch } }));

  const setConfig = (section, patch) =>
    setState((s) => ({
      ...s,
      coupon: {
        ...s.coupon,
        config: {
          ...s.coupon.config,
          [section]: { ...s.coupon.config[section], ...patch },
        },
      },
    }));

  const setDiscount = (patch) => {
    setState((s) => ({
      ...s,
      coupon: {
        ...s.coupon,
        config: {
          ...s.coupon.config,
          discount: { ...s.coupon.config.discount, ...patch },
        },
      },
    }));
  };

  const setDiscountType = (t) => {
    let discount;
    if (t === "percent") discount = { kind: "percent", value: 20, max_cap: null };
    else if (t === "flat") discount = { kind: "flat", value: 100 };
    else if (t === "bogo") discount = { kind: "bogo", buy_qty: 1, get_qty: 1, get_type: "free" };
    else discount = { kind: "tiered", slabs: [{ min_spend: 1000, off_value: 100, off_type: "flat" }] };
    setState((s) => ({
      ...s,
      coupon: {
        ...s.coupon,
        discount_type: t,
        config: { ...s.coupon.config, discount },
      },
    }));
  };

  const setSingle = (patch) =>
    setState((s) => ({ ...s, single: { ...s.single, ...patch } }));

  const setSeriesConfig = (patch) =>
    setState((s) => ({
      ...s,
      series: {
        ...s.series,
        generation_config: { ...s.series.generation_config, ...patch },
      },
    }));

  const limits = state.coupon.config.limits;
  const scope = state.coupon.config.scope;
  const stacking = state.coupon.config.stacking;
  const returnPolicy = state.coupon.config.return_policy;
  const discount = state.coupon.config.discount;

  const validation = useMemo(() => {
    const errs = [];
    if (!state.coupon.name.trim()) errs.push("Coupon name is required");
    if (state.coupon.valid_to < state.coupon.valid_from)
      errs.push("End date must be after start date");
    if (state.mode === "single" && !state.single.code.trim())
      errs.push("Code is required for single coupons");
    if (state.mode === "series") {
      const g = state.series.generation_config;
      if (!g.series_name.trim()) errs.push("Series name is required");
      if (!g.generation_quantity || g.generation_quantity <= 0)
        errs.push("Generation quantity must be > 0");
      if (!g.code_length || g.code_length < 4) errs.push("Code length must be at least 4");
    }
    return errs;
  }, [state]);

  const handleSave = () => {
    if (validation.length > 0) {
      errorToast(validation[0]);
      return;
    }
    const envelope = buildEnvelope(state);
    setSaving(true);
    if (isEdit) {
      dispatch(
        couponUpdateAction(
          { id: editing.id, body: envelope },
          () => {
            setSaving(false);
            successToast("Coupon updated");
            onSaved?.();
          },
          (err) => {
            setSaving(false);
            errorToast(err?.message || "Failed to update coupon");
          }
        )
      );
    } else {
      dispatch(
        couponCreateAction(
          envelope,
          () => {
            setSaving(false);
            successToast(
              state.mode === "series"
                ? "Series queued — generation will start shortly"
                : "Coupon created"
            );
            onSaved?.();
          },
          (err) => {
            setSaving(false);
            errorToast(err?.message || "Failed to create coupon");
          }
        )
      );
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-[17px] font-bold text-[#0F172A]">
            {isEdit ? "Edit coupon" : "Create coupon"}
          </h2>
          <p className="text-[13px] text-[#64748B] mt-1 max-w-[720px]">
            One form, two modes. Use <b>Single</b> for one shared code that anyone can use; use <b>Series</b> to generate thousands of unique codes for targeted distribution.
          </p>
        </div>
        <Seg
          value={state.mode}
          onChange={setMode}
          options={[
            { value: "single", label: "Single", icon: <Ticket size={14} /> },
            { value: "series", label: "Series", icon: <Layers size={14} /> },
          ]}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-5">
        <div className="space-y-4">
          {/* Identity */}
          <Card>
            <CardHeader
              icon={<Sparkles size={18} />}
              tone="blue"
              title="Identity"
              desc="Name, status and the underlying discount"
            />
            <CardBody>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Coupon name" required>
                  <Input
                    placeholder="Monsoon Sale 2026"
                    value={state.coupon.name}
                    onChange={(e) => setCoupon({ name: e.target.value })}
                  />
                </Field>
                <Field label="Status">
                  <Select
                    value={state.coupon.status}
                    onChange={(e) => setCoupon({ status: e.target.value })}
                  >
                    {STATUS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Description" className="md:col-span-2">
                  <Input
                    placeholder="Optional — shows in the admin list"
                    value={state.coupon.description}
                    onChange={(e) => setCoupon({ description: e.target.value })}
                  />
                </Field>
              </div>

              <div className="h-px bg-[#EEF1F5] my-4" />

              <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-4 items-start">
                <Field label="Discount type">
                  <Select
                    value={state.coupon.discount_type}
                    onChange={(e) => setDiscountType(e.target.value)}
                  >
                    {DISCOUNT_TYPES.map((d) => (
                      <option key={d.value} value={d.value}>
                        {d.label}
                      </option>
                    ))}
                  </Select>
                </Field>

                {state.coupon.discount_type === "percent" && (
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Discount %">
                      <Affix post="%">
                        <Input
                          type="number"
                          value={discount.value ?? 0}
                          onChange={(e) => setDiscount({ value: Number(e.target.value) })}
                        />
                      </Affix>
                    </Field>
                    <Field label="Max cap (₹)" help="Optional — leave blank for no cap.">
                      <Affix pre="₹">
                        <Input
                          type="number"
                          value={discount.max_cap ?? ""}
                          onChange={(e) =>
                            setDiscount({
                              max_cap:
                                e.target.value === "" ? null : Number(e.target.value),
                            })
                          }
                          style={{ paddingLeft: 28 }}
                        />
                      </Affix>
                    </Field>
                  </div>
                )}

                {state.coupon.discount_type === "flat" && (
                  <Field label="Flat amount off">
                    <Affix pre="₹">
                      <Input
                        type="number"
                        value={discount.value ?? 0}
                        onChange={(e) => setDiscount({ value: Number(e.target.value) })}
                        style={{ paddingLeft: 28 }}
                      />
                    </Affix>
                  </Field>
                )}

                {state.coupon.discount_type === "bogo" && (
                  <div className="grid grid-cols-3 gap-3">
                    <Field label="Buy qty">
                      <Input
                        type="number"
                        value={discount.buy_qty ?? 1}
                        onChange={(e) => setDiscount({ buy_qty: Number(e.target.value) })}
                      />
                    </Field>
                    <Field label="Get qty">
                      <Input
                        type="number"
                        value={discount.get_qty ?? 1}
                        onChange={(e) => setDiscount({ get_qty: Number(e.target.value) })}
                      />
                    </Field>
                    <Field label="Get type">
                      <Select
                        value={discount.get_type || "free"}
                        onChange={(e) => setDiscount({ get_type: e.target.value })}
                      >
                        <option value="free">Free</option>
                        <option value="half">Half price</option>
                      </Select>
                    </Field>
                  </div>
                )}

                {state.coupon.discount_type === "tiered" && (
                  <div className="text-[12px] text-[#64748B]">
                    Tiered slabs are authored from the JSON config in v1. UI editor follows.
                  </div>
                )}
              </div>
            </CardBody>
          </Card>

          {/* Validity */}
          <Card>
            <CardHeader title="Validity" desc="Window during which this coupon can be redeemed" />
            <CardBody>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Valid from" required>
                  <Input
                    type="date"
                    value={msToInputDate(state.coupon.valid_from)}
                    onChange={(e) => setCoupon({ valid_from: inputDateToMs(e.target.value) })}
                  />
                </Field>
                <Field label="Valid to" required>
                  <Input
                    type="date"
                    value={msToInputDate(state.coupon.valid_to)}
                    onChange={(e) =>
                      setCoupon({ valid_to: inputDateToMs(e.target.value, true) })
                    }
                  />
                </Field>
              </div>
            </CardBody>
          </Card>

          {/* Limits */}
          <Card>
            <CardHeader icon={<Lock size={18} />} tone="amber" title="Limits" />
            <CardBody>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Max uses (total)" help="Leave blank for no global cap.">
                  <Input
                    type="number"
                    value={limits.max_uses ?? ""}
                    onChange={(e) =>
                      setConfig("limits", {
                        max_uses: e.target.value === "" ? null : Number(e.target.value),
                      })
                    }
                  />
                </Field>
                <Field label="Per-user limit" help="Times a single customer can redeem.">
                  <Input
                    type="number"
                    value={limits.per_user_limit ?? 1}
                    onChange={(e) =>
                      setConfig("limits", { per_user_limit: Number(e.target.value) })
                    }
                    disabled={!limits.per_user}
                  />
                </Field>
                <Field label="Min. purchase (₹)" help="Bill below this is rejected at billing.">
                  <Affix pre="₹">
                    <Input
                      type="number"
                      value={limits.min_purchase ?? 0}
                      onChange={(e) =>
                        setConfig("limits", { min_purchase: Number(e.target.value) })
                      }
                      style={{ paddingLeft: 28 }}
                    />
                  </Affix>
                </Field>
                <Field label="Min. quantity" help="Min line-item count on the bill.">
                  <Input
                    type="number"
                    value={limits.min_qty ?? 0}
                    onChange={(e) =>
                      setConfig("limits", { min_qty: Number(e.target.value) })
                    }
                  />
                </Field>
              </div>
              <div className="mt-2">
                <ToggleRow
                  title="Enforce per-user cap"
                  desc="Track redemptions per customer and block repeat use once limit is hit."
                  on={!!limits.per_user}
                  onChange={(v) => setConfig("limits", { per_user: v })}
                />
              </div>
            </CardBody>
          </Card>

          {/* Scope */}
          <Card>
            <CardHeader title="Scope" desc="Where this coupon applies and to whom" />
            <CardBody>
              <Field label="Eligible products">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {SCOPE_OPTIONS.map((o) => (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() => setConfig("scope", { type: o.value })}
                      className={`flex items-center gap-2 p-3 rounded-lg border text-left transition-colors ${
                        scope.type === o.value
                          ? "border-primary bg-primary/5"
                          : "border-[#E6E9EF] hover:bg-[#F8FAFC]"
                      }`}
                    >
                      <span
                        className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                          scope.type === o.value ? "border-primary" : "border-[#CBD5E1]"
                        }`}
                      >
                        {scope.type === o.value && (
                          <span className="w-2 h-2 rounded-full bg-primary" />
                        )}
                      </span>
                      <span className="text-[13px] font-semibold text-[#0F172A]">
                        {o.label}
                      </span>
                    </button>
                  ))}
                </div>
              </Field>

              <div className="grid grid-cols-2 gap-4 mt-4">
                <Field label="Eligible tiers" help="Comma-separated tier IDs, or ALL.">
                  <Input
                    value={(scope.eligible_tiers || []).join(", ")}
                    onChange={(e) =>
                      setConfig("scope", {
                        eligible_tiers: e.target.value
                          .split(",")
                          .map((s) => s.trim())
                          .filter(Boolean),
                      })
                    }
                  />
                </Field>
                <div className="flex items-end pb-1">
                  <ToggleRow
                    title="First purchase only"
                    desc="Limit to a customer's very first transaction."
                    on={!!scope.first_purchase_only}
                    onChange={(v) => setConfig("scope", { first_purchase_only: v })}
                  />
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Stacking + return */}
          <Card>
            <CardHeader icon={<Layers size={18} />} tone="purple" title="Stacking & return policy" />
            <CardBody className="py-1">
              <ToggleRow
                title="Stack with other coupons"
                desc="Allow combining with another coupon on the same bill."
                on={!!stacking.with_other_coupons}
                onChange={(v) => setConfig("stacking", { with_other_coupons: v })}
              />
              <ToggleRow
                title="Stack with loyalty points"
                desc="Customer can also redeem points on the same bill."
                on={!!stacking.with_points}
                onChange={(v) => setConfig("stacking", { with_points: v })}
              />
              <ToggleRow
                title="Earn points on this purchase"
                desc="If off, items sold via this coupon won't accrue points."
                on={!!stacking.earn_on_this}
                onChange={(v) => setConfig("stacking", { earn_on_this: v })}
              />
              <ToggleRow
                title="Non-returnable"
                desc="Items bought via this coupon cannot be returned."
                on={!!returnPolicy.non_returnable}
                onChange={(v) => setConfig("return_policy", { non_returnable: v })}
              />
              <ToggleRow
                title="No exchange"
                desc="Items bought via this coupon cannot be exchanged."
                on={!!returnPolicy.no_exchange}
                onChange={(v) => setConfig("return_policy", { no_exchange: v })}
              />
              <ToggleRow
                title="Show warning at billing"
                desc="Cashier sees a banner explaining the policy at checkout."
                on={!!returnPolicy.show_warning_at_billing}
                onChange={(v) => setConfig("return_policy", { show_warning_at_billing: v })}
              />
            </CardBody>
          </Card>

          {/* SINGLE-MODE BLOCK */}
          {state.mode === "single" && (
            <Card>
              <CardHeader
                icon={<Ticket size={18} />}
                tone="blue"
                title="Single code"
                desc="One shared code, used by many customers"
              />
              <CardBody>
                <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-end">
                  <Field label="Code" required help="Uppercase letters, digits, dashes and underscores.">
                    <Input
                      mono
                      placeholder="MONSOON20"
                      value={state.single.code}
                      onChange={(e) => setSingle({ code: upperSlug(e.target.value) })}
                    />
                  </Field>
                  <div className="pb-1">
                    <ToggleRow
                      title="Case-insensitive lookup"
                      desc="Cashier may type the code in any case."
                      on={!!state.single.case_insensitive}
                      onChange={(v) => setSingle({ case_insensitive: v })}
                    />
                  </div>
                </div>
              </CardBody>
            </Card>
          )}

          {/* SERIES-MODE BLOCK */}
          {state.mode === "series" && (
            <Card>
              <CardHeader
                icon={<Send size={18} />}
                tone="purple"
                title="Series generation"
                desc="Generate N unique codes for targeted distribution"
              />
              <CardBody>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Series name" required>
                    <Input
                      placeholder="Birthday Q3 2026"
                      value={state.series.generation_config.series_name}
                      onChange={(e) => setSeriesConfig({ series_name: e.target.value })}
                    />
                  </Field>
                  <Field label="Distribution channel">
                    <Select
                      value={state.series.generation_config.distribution_channel}
                      onChange={(e) =>
                        setSeriesConfig({ distribution_channel: e.target.value })
                      }
                    >
                      {CHANNELS.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field label="Code prefix" help="Optional. Each code starts with this string.">
                    <Input
                      mono
                      placeholder="BIRTHDAY-"
                      value={state.series.generation_config.prefix}
                      onChange={(e) =>
                        setSeriesConfig({ prefix: e.target.value.toUpperCase() })
                      }
                    />
                  </Field>
                  <Field label="Code length" help="Random part after the prefix.">
                    <Input
                      type="number"
                      value={state.series.generation_config.code_length}
                      onChange={(e) =>
                        setSeriesConfig({ code_length: Number(e.target.value) })
                      }
                    />
                  </Field>
                  <Field label="Charset">
                    <Select
                      value={state.series.generation_config.code_charset}
                      onChange={(e) => setSeriesConfig({ code_charset: e.target.value })}
                    >
                      {CHARSETS.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field label="Generation quantity" required>
                    <Input
                      type="number"
                      value={state.series.generation_config.generation_quantity}
                      onChange={(e) =>
                        setSeriesConfig({ generation_quantity: Number(e.target.value) })
                      }
                    />
                  </Field>
                </div>
                <div className="mt-4 p-3 rounded-lg bg-[#F8FAFC] border border-[#E6E9EF] text-[12.5px] text-[#475569]">
                  <RefreshCw size={14} className="inline mr-1.5 -mt-0.5" />
                  Generation runs in-process. The form returns immediately and the list view
                  shows live progress (<span className="font-mono">codes_generated / target</span>) until the series flips to <b>active</b>.
                </div>
              </CardBody>
            </Card>
          )}
        </div>

        {/* Right-rail preview */}
        <div className="space-y-4">
          <Card>
            <CardHeader title="Preview" desc="How this coupon will appear" />
            <CardBody>
              <div className="text-[12px] text-[#94A3B8]">Coupon</div>
              <div className="text-[16px] font-bold text-[#0F172A] mt-0.5">
                {state.coupon.name || "Untitled coupon"}
              </div>
              <div className="mt-3 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-primary/10 text-primary">
                {state.mode === "single" ? <Ticket size={11} /> : <Layers size={11} />}
                {state.mode === "single" ? "Single code" : "Series"}
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 text-[12px]">
                <div>
                  <div className="text-[#94A3B8]">Discount</div>
                  <div className="font-semibold text-[#0F172A] mt-0.5">
                    {state.coupon.discount_type === "percent"
                      ? `${discount.value ?? 0}%${discount.max_cap ? ` (max ₹${discount.max_cap})` : ""}`
                      : state.coupon.discount_type === "flat"
                      ? `₹${discount.value ?? 0} off`
                      : state.coupon.discount_type === "bogo"
                      ? `Buy ${discount.buy_qty ?? 1}, get ${discount.get_qty ?? 1}`
                      : "Tiered"}
                  </div>
                </div>
                <div>
                  <div className="text-[#94A3B8]">Validity</div>
                  <div className="font-semibold text-[#0F172A] mt-0.5">
                    {msToInputDate(state.coupon.valid_from)} → {msToInputDate(state.coupon.valid_to)}
                  </div>
                </div>
                <div>
                  <div className="text-[#94A3B8]">Max uses</div>
                  <div className="font-semibold text-[#0F172A] mt-0.5">
                    {limits.max_uses ? limits.max_uses : "Unlimited"}
                  </div>
                </div>
                <div>
                  <div className="text-[#94A3B8]">Per-user</div>
                  <div className="font-semibold text-[#0F172A] mt-0.5">
                    {limits.per_user ? `${limits.per_user_limit ?? 1}×` : "Not capped"}
                  </div>
                </div>
              </div>
              {state.mode === "single" && (
                <div className="mt-4 p-2 rounded-md bg-[#0F172A] text-white font-mono text-center tracking-widest">
                  {state.single.code || "ENTER-CODE"}
                </div>
              )}
              {state.mode === "series" && (
                <div className="mt-4 p-2 rounded-md bg-[#0F172A] text-white font-mono text-center text-[12px]">
                  {state.series.generation_config.prefix || ""}
                  <span className="opacity-60">
                    {"X".repeat(state.series.generation_config.code_length || 8)}
                  </span>
                  <span className="block text-[10px] mt-1 opacity-50">
                    × {Number(state.series.generation_config.generation_quantity || 0).toLocaleString("en-IN")} codes
                  </span>
                </div>
              )}
            </CardBody>
          </Card>
        </div>
      </div>

      <FormBar
        hint={
          validation.length > 0
            ? validation[0]
            : "All set — review the preview before saving."
        }
        onCancel={onCancel}
        onSave={handleSave}
        saving={saving}
        saveLabel={
          isEdit
            ? "Save changes"
            : state.mode === "series"
            ? "Create & queue generation"
            : "Create coupon"
        }
        disabled={validation.length > 0}
      />
    </div>
  );
};

export default CreateTab;
